import React, { useState, useEffect } from "react";
import {
  Settings,
  Trophy,
  Gamepad2,
  Star,
  Coins,
  PlayCircle,
  ShoppingBag,
} from "lucide-react";
import { apiRequest } from "../utils/api";
import AvatarImg from "../components/AvatarImg";

const Profile = () => {
  const [userData, setUserData] = useState(null);
  const [recentGames, setRecentGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coinData, setCoinData] = useState({
    coinBalance: 0,
    recentTransactions: [],
  });
  const [isActionLoading, setIsActionLoading] = useState(false);

  const token = localStorage.getItem("accessToken");

  // Listen for real-time avatar updates
  useEffect(() => {
    const handleAvatarUpdate = (e) => {
      const newAvatarUrl = e.detail;
      setUserData((prev) => prev ? { ...prev, avatarUrl: newAvatarUrl } : prev);
    };
    window.addEventListener("avatar-update", handleAvatarUpdate);
    return () => window.removeEventListener("avatar-update", handleAvatarUpdate);
  }, []);

  useEffect(() => {
    const fetchProfileAndGames = async () => {
      if (!token) return;
      try {
        // 1. Fetch Basic Profile
        const profileRes = await apiRequest('/players/me');
        if (profileRes.ok) {
          const profileData = await profileRes.json();
          setUserData(profileData);

          // 2. Fetch Real Games using the gamerName from the profile response
          const gamesRes = await apiRequest(`/players/${profileData.gamerName}/games`);
          if (gamesRes.ok) {
            const gamesData = await gamesRes.json();
            setRecentGames(gamesData);
          }
        }

        // 3. Fetch Coin Balance & Transactions
        const coinRes = await apiRequest('/Coins/balance');
        if (coinRes.ok) {
          const coins = await coinRes.json();
          setCoinData(coins);
        }
      } catch (error) {
        console.error("Failed to load profile data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfileAndGames();
  }, [token]);

  // Fix #5: Removed client-controlled coinsEarned from request body.
  // The backend determines the reward amount — client sends only the trigger action.
  const handleWatchAd = async () => {
    setIsActionLoading(true);
    try {
      const res = await apiRequest('/Coins/watch-ad', {
        method: "POST",
        body: JSON.stringify({
          adDescription: "Video Reward Ad",
          // NOTE: coinsEarned is intentionally NOT sent — server must compute this
        }),
      });
      if (res.ok) {
        const data = await res.json();
        updateLocalCoins(data.newCoinBalance);
        alert(`Rewarded! You earned ${data.coinsEarned} coins.`);
      }
    } catch (err) {
      alert("Ad system currently unavailable.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleSpendCoins = async () => {
    if (coinData.coinBalance < 100) return alert("Insufficient coins!");
    setIsActionLoading(true);
    try {
      const res = await apiRequest('/Coins/spend', {
        method: "POST",
        body: JSON.stringify({
          amount: 100,
          reason: "Exclusive Profile Badge",
        }),
      });
      if (res.ok) {
        const data = await res.json();
        updateLocalCoins(data.newCoinBalance);
        alert(`Success! Spent ${data.coinsSpent} coins.`);
      }
    } catch (err) {
      alert("Transaction failed.");
    } finally {
      setIsActionLoading(false);
    }
  };

  const updateLocalCoins = (newBalance) => {
    setCoinData((prev) => ({ ...prev, coinBalance: newBalance }));
    window.dispatchEvent(
      new CustomEvent("coin-update", { detail: newBalance }),
    );
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  if (loading)
    return (
      <div className="p-20 text-center font-bold animate-pulse">
        SYNCHRONIZING PROFILE...
      </div>
    );
  if (!userData)
    return <div className="p-20 text-center">Unauthorized access.</div>;

  const nextLevelXP = 200000;
  const xpProgress = (userData.experiencePoints / nextLevelXP) * 100;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6 animate-in fade-in duration-500">
      {/* Profile Header */}
      <div className="glass-panel p-8 flex flex-col md:flex-row gap-8 items-center rounded-2xl relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-r from-game-accent/5 to-game-neon/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
        <div className="w-32 h-32 bg-game-dark flex items-center justify-center border border-game-border shadow-[0_0_15px_rgba(255,94,14,0.2)] group-hover:shadow-[0_0_25px_rgba(255,94,14,0.5)] transition-all rounded-2xl relative z-10 overflow-hidden p-3 group/avatar">
          <AvatarImg
            src={userData.avatarUrl || userData.avatarId}
            alt={userData.gamerName}
            className="w-full h-full object-contain filter drop-shadow-[0_0_10px_rgba(255,184,0,0.4)]"
            fallback={
              <div className="text-4xl font-black text-game-accent uppercase">
                {userData.gamerName.charAt(0)}
              </div>
            }
          />
        </div>
        <div className="flex-1 w-full space-y-4">
          <div className="flex justify-between items-start relative z-10">
            <div>
              <h2 className="text-3xl font-heading font-black italic uppercase tracking-tighter text-white">
                {userData.gamerName}
              </h2>
              <div className="flex gap-4 mt-1 text-[10px] font-bold uppercase tracking-widest text-game-muted">
                <span className="bg-gradient-to-r from-game-accent to-game-neon text-white px-3 py-0.5 rounded-full shadow-[0_0_10px_rgba(255,184,0,0.3)]">
                  Level {userData.experienceLevel}
                </span>
                <span className="py-0.5">
                  Joined {new Date(userData.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
            <button className="p-2 border border-game-border hover:border-game-accent hover:text-game-neon rounded-full transition-all hover:bg-white/5 hover:shadow-[0_0_15px_rgba(255,94,14,0.2)] text-game-muted cursor-pointer">
              <Settings size={18} />
            </button>
          </div>
          <div className="space-y-1 relative z-10">
            <div className="w-full bg-game-dark h-2 rounded-full overflow-hidden border border-white/5">
              <div
                className="bg-gradient-to-r from-game-accent to-game-neon h-full transition-all duration-1000 relative shadow-[0_0_10px_rgba(255,184,0,0.8)]"
                style={{ width: `${xpProgress}%` }}
              >
                <div className="absolute top-0 right-0 bottom-0 w-2 bg-white/50 blur-[2px]"></div>
              </div>
            </div>
            <div className="flex justify-between text-[10px] font-bold text-game-muted uppercase tracking-tighter">
              <span>{userData.experiencePoints.toLocaleString()} XP</span>
              <span>{nextLevelXP.toLocaleString()} XP to Next Level</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Gamepad2 size={20} />}
          label="Games Played"
          value={userData.gamesPlayedCount}
        />
        <StatCard
          icon={<Trophy size={20} />}
          label="Wins"
          value={userData.winsCount}
        />
        <StatCard
          icon={<Coins size={20} className="text-yellow-500" />}
          label="Coins"
          value={coinData.coinBalance.toLocaleString()}
        />
        <StatCard
          icon={<Star size={20} className="fill-yellow-400 text-yellow-400" />}
          label="Total Score"
          value={userData.totalScore.toLocaleString()}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Coin Vault */}
          <div className="space-y-4">
            <h3 className="text-xs font-heading font-black uppercase tracking-widest border-l-4 border-yellow-500 pl-2 text-white">
              Coin Vault
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-game-card border border-game-border p-6 flex flex-col items-center justify-center gap-3 rounded-2xl hover:border-[#FF5E0E]/50 hover:shadow-[0_0_15px_rgba(255,94,14,0.2)] transition-all">
                <PlayCircle size={32} className="text-[#FF5E0E]" />
                <div className="text-center">
                  <div className="text-[10px] font-black uppercase text-white">
                    Watch Reward Ad
                  </div>
                  <div className="text-[8px] text-game-accent font-bold uppercase tracking-widest">
                    +COINS
                  </div>
                </div>
                <button
                  disabled={isActionLoading}
                  onClick={handleWatchAd}
                  className="w-full py-2 game-btn rounded-xl text-[10px]"
                >
                  {isActionLoading ? "Wait..." : "Earn Coins"}
                </button>
              </div>
              <div className="bg-game-card border border-game-border p-6 flex flex-col items-center justify-center gap-3 rounded-2xl hover:border-red-500/50 hover:shadow-[0_0_15px_rgba(239,68,68,0.2)] transition-all">
                <ShoppingBag size={32} className="text-rose-500 drop-shadow-[0_0_8px_rgba(244,63,94,0.5)]" />
                <div className="text-center">
                  <div className="text-[10px] font-black uppercase text-white">
                    Spend Coins
                  </div>
                  <div className="text-[8px] text-rose-400 font-bold uppercase tracking-widest">
                    -100 COINS
                  </div>
                </div>
                <button
                  disabled={isActionLoading || coinData.coinBalance < 100}
                  onClick={handleSpendCoins}
                  className="w-full py-2 border border-game-border text-[10px] font-black uppercase tracking-widest hover:bg-rose-500/10 hover:text-rose-400 hover:border-rose-500/50 transition-all rounded-xl text-game-muted"
                >
                  Shop Reward
                </button>
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="space-y-4">
            <h3 className="text-xs font-heading font-black uppercase tracking-widest border-l-4 border-game-neon pl-2 text-white">
              Achievements
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <AchievementItem icon="🏆" title="First Victory" color="bg-orange-50" />
              <AchievementItem icon="🔥" title="Win Streak" color="bg-orange-50" />
            </div>
          </div>

          {/* Recent Games */}
          <div className="space-y-4">
            <h3 className="text-xs font-heading font-black uppercase tracking-widest border-l-4 border-game-accent pl-2 text-white">
              Recent Games
            </h3>
            <div className="border border-game-border bg-game-card divide-y divide-game-border/50 rounded-2xl overflow-hidden shadow-lg">
              {recentGames.length > 0 ? (
                recentGames.map((game) => (
                  <div
                    key={game.gameSessionId}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-game-dark border border-game-border rounded-xl flex items-center justify-center text-game-accent shadow-inner">
                        <Gamepad2 size={24} />
                      </div>
                      <div>
                        <div className="text-sm font-heading font-bold uppercase tracking-tight text-white hover:text-game-neon cursor-pointer transition-colors">
                          {game.gameName}
                        </div>
                        <div className="text-[10px] text-game-muted font-medium">
                          {getTimeAgo(game.playedAt)}
                        </div>
                      </div>
                    </div>
                    <div className="text-game-neon font-black text-sm bg-game-neon/10 px-3 py-1 rounded-full border border-game-neon/20 shadow-[0_0_10px_rgba(255,184,0,0.2)]">
                      +{game.xpEarned} XP
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-10 text-center text-game-muted text-xs italic">
                  No battle records found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Friends & Ledger */}
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-xs font-heading font-black uppercase tracking-widest border-l-4 border-game-neon pl-2 text-white">
              Friends
            </h3>
            <div className="border border-game-border bg-game-card p-4 space-y-2 rounded-2xl shadow-lg">
              <FriendLine name="GamerPro" status="Online" color="bg-green-500" avatarUrl="wolf" />
              <FriendLine name="SkillMaster" status="In-Game" color="bg-yellow-500" avatarUrl="fire" />
              <FriendLine name="NinjaStar" status="Offline" color="bg-gray-300" avatarUrl="robot" />
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-xs font-heading font-black uppercase tracking-widest border-l-4 border-yellow-500 pl-2 text-white">
              Bank Ledger
            </h3>
            <div className="border border-game-border bg-game-card p-4 divide-y divide-white/5 rounded-2xl shadow-lg">
              {coinData.recentTransactions?.length > 0 ? (
                coinData.recentTransactions.slice(0, 5).map((t) => (
                  <div
                    key={t.id}
                    className="py-3 flex justify-between items-center hover:bg-white/5 px-2 -mx-2 rounded-lg transition-colors"
                  >
                    <div className="text-[9px] font-bold uppercase text-game-muted">
                      {t.description}
                    </div>
                    <div
                      className={`text-xs font-black ${t.amount >= 0 ? "text-green-400 drop-shadow-[0_0_5px_rgba(74,222,128,0.5)]" : "text-rose-400 drop-shadow-[0_0_5px_rgba(251,113,133,0.5)]"}`}
                    >
                      {t.amount >= 0 ? "+" : ""}
                      {t.amount}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-[10px] text-game-muted italic py-4 text-center">
                  No transaction history.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Components
const StatCard = ({ icon, label, value }) => (
  <div className="bg-game-card border border-game-border rounded-2xl p-6 flex flex-col items-center justify-center space-y-1 hover:border-game-accent hover:shadow-[0_0_15px_rgba(255,94,14,0.2)] hover:-translate-y-1 transition-all">
    <div className="text-game-muted mb-1 drop-shadow-md">{icon}</div>
    <div className="text-2xl font-black italic tracking-tighter text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]">{value}</div>
    <div className="text-[9px] text-game-accent uppercase font-black tracking-widest">
      {label}
    </div>
  </div>
);

const AchievementItem = ({ icon, title, color }) => {
  const getGlow = () => {
    if (color.includes('orange')) return 'shadow-[0_0_15px_rgba(255,94,14,0.2)] border-[#FF5E0E]/30 bg-[#FF5E0E]/5 text-[#FFB800] hover:shadow-[0_0_20px_rgba(255,94,14,0.4)] hover:-translate-y-1';
    return 'shadow-[0_0_15px_rgba(255,94,14,0.2)] border-[#FF5E0E]/30 bg-[#FF5E0E]/5 text-[#FFB800] hover:shadow-[0_0_20px_rgba(255,94,14,0.4)] hover:-translate-y-1';
  };

  return (
    <div className={`border rounded-xl p-6 text-center space-y-2 transition-all cursor-pointer ${getGlow()}`}>
      <div className="text-3xl drop-shadow-lg scale-110">{icon}</div>
      <div className="text-[10px] font-black uppercase tracking-widest text-inherit mt-2">
        {title}
      </div>
    </div>
  );
};

const FriendLine = ({ name, status, color, avatarUrl }) => (
  <div className="flex items-center justify-between group cursor-pointer p-2 hover:bg-white/5 rounded-lg transition-colors">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 bg-game-dark border border-game-border rounded-full flex items-center justify-center text-[10px] font-black uppercase text-game-accent group-hover:shadow-[0_0_10px_rgba(255,94,14,0.4)] transition-shadow overflow-hidden p-1">
        <AvatarImg
          src={avatarUrl}
          alt={name}
          className="w-full h-full object-contain"
          fallback={<span>{name.charAt(0)}</span>}
        />
      </div>
      <div>
        <div className="text-xs font-bold group-hover:text-game-neon text-white uppercase tracking-tight transition-colors">
          {name}
        </div>
        <div className="text-[9px] text-game-muted uppercase font-bold">
          {status}
        </div>
      </div>
    </div>
    <div className={`w-2 h-2 rounded-full ${color}`}></div>
  </div>
);

export default Profile;
